import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { z } from 'zod';
import Modal from '../../components/Modal';
import CustomInput from '../../components/CustomInput';
import ActionButtons from '../../components/ActionButtons';
import { INITIAL_FORM_STATE, INITIAL_ERROR_STATE, getFormConfig, CONSTITUTION_SCHEMA } from './config';

const RoleModal = ({ tr, isOpen, onClose, isEdit = false, selectedItem, setIsEdit, onSubmit, status }) => {
    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [errors, setErrors] = useState(INITIAL_ERROR_STATE);

    const handleInputChange = useCallback((name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    }, []);

    // Validate form using Zod
    const validateForm = useCallback(() => {
        try {
            CONSTITUTION_SCHEMA.parse(formData);
            setErrors(INITIAL_ERROR_STATE);
            return true;
        } catch (error) {
            if (error instanceof z.ZodError) {
                const newErrors = {};
                error.errors.forEach((err) => {
                    newErrors[err.path[0]] = err.message;
                });
                setErrors(newErrors);
            }
            return false;
        }
    }, [formData]);

    useEffect(() => {
        if (isOpen && isEdit && selectedItem) {
            setFormData({
                name: selectedItem.name || '',
                id: selectedItem.id || '',
                weight: selectedItem.weight || '',
                description: selectedItem.description || ''
            });
        } else if (!isOpen) {
            setFormData(INITIAL_FORM_STATE);
        }
    }, [isOpen, isEdit, selectedItem]);

    const formConfig = useMemo(() => getFormConfig(formData, handleInputChange, errors), [formData, handleInputChange, errors]);
    const handleSubmit = useCallback(
        (e) => {
            e.preventDefault();

            if (!validateForm()) {
                return;
            }

            if (typeof onSubmit === 'function') {
                const formDataToSend = { ...formData };

                if (isEdit) {
                    formDataToSend.id = selectedItem?.id;
                }

                onSubmit(formDataToSend);
            }
        },
        [isEdit, formData, onSubmit, selectedItem, validateForm]
    );
    // Memoize labels
    const labels = useMemo(
        () => ({
            normalLabel: isEdit ? tr('modal.edit') : tr('modal.create'),
            loadingLabel: isEdit ? tr('modal.loadingEdit') : tr('modal.loadingCreate')
        }),
        [isEdit, tr]
    );

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? tr('modal.titleEdit') : tr('modal.titleCreate')}>
            <form onSubmit={handleSubmit} className="space-y-4">
                {formConfig.map((item) => (
                    <CustomInput key={item.id} item={item} />
                ))}
                <ActionButtons status={status} onClose={onClose} onSubmit={handleSubmit} isEdit={isEdit} label={labels} />
            </form>
        </Modal>
    );
};

export default React.memo(RoleModal);
